import { Dialog } from "prism-react";
import { useState } from "react";

import type { GlobalModalContext } from "./useGlobalModal";
import { context } from "./useGlobalModal";

export type GlobalModalProps = {
  open: boolean;
  maxWidth?: number;
  closeOnInteractOutside?: boolean;
  disablePadding?: boolean;
};

/**
 * This component can be used standalone to be mounted in any component. It is also
 * used by the useGlobalModal hook for mounting ad-hoc
 */
export const GlobalModal: React.FC<{
  children: React.ReactNode;
  open: boolean;
  maxWidth: number;
  fixedHeight?: number;
  disablePadding?: boolean;
  onInteractOutside?: (event: { preventDefault: () => void }) => void;
  onOpenChange?: (open: boolean) => void;
}> = ({
  open,
  maxWidth,
  fixedHeight,
  onInteractOutside = (event) => event.preventDefault(),
  children,
  onOpenChange = () => {},
  disablePadding,
}) => {
  return (
    <Dialog
      contentProps={{
        onInteractOutside,
        css: {
          maxWidth: maxWidth || 500,
          height: fixedHeight ? `${fixedHeight}px` : "auto",
          userSelect: "text",
          padding: disablePadding ? 0 : undefined,
        },
      }}
      onOpenChange={onOpenChange}
      open={open}
    >
      {children}
    </Dialog>
  );
};

export const GlobalModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<GlobalModalContext>({
    open: false,
    children: null,
  });
  return (
    <context.Provider value={setState}>
      {children}
      <GlobalModal
        disablePadding={state.disablePadding || false}
        maxWidth={state.maxWidth || 500}
        onInteractOutside={(e) => {
          if (state.closeOnInteractOutside) {
            setState((current) => ({
              ...current,
              open: false,
            }));
          } else {
            e.preventDefault();
          }
        }}
        open={state.open}
      >
        {state.children}
      </GlobalModal>
    </context.Provider>
  );
};
